import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { createOssOrder, updateOssOrder } from "../api/ossApi";

const today = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({
  itemCode: "",
  itemName: "",
  specification: "",
  quantity: 1,
  unitCode: "EA",
  requestedDueDate: today(),
  standardLeadDays: 10,
  planningBufferDays: 5,
  dayBasis: "BUSINESS",
  productionPlanStatus: "NOT_REVIEWED",
});

const makeOrderNo = () => {
  const now = new Date();
  const ymd = now.toISOString().slice(0, 10).replaceAll("-", "");
  return `ORD-${ymd}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
};

const emptyForm = () => ({
  orderNo: makeOrderNo(),
  customerName: "",
  siteName: "",
  customerPoNo: "",
  orderDate: today(),
  orderStatus: "RECEIVED",
  note: "",
  lines: [emptyLine()],
});

const normalizeDetailToForm = (detail) => {
  if (!detail?.order) return emptyForm();
  return {
    orderNo: detail.order.order_no || "",
    customerName: detail.order.customer_name || "",
    siteName: detail.order.site_name === "-" ? "" : detail.order.site_name || "",
    customerPoNo: detail.order.customer_po_no || "",
    orderDate: detail.order.order_date || today(),
    orderStatus: detail.order.order_status || "RECEIVED",
    note: detail.order.note || "",
    lines: (detail.lines || []).map((line, index) => ({
      orderLineId: line.order_line_id,
      lineNo: line.line_no || index + 1,
      itemCode: line.item_code_snapshot || "",
      itemName: line.item_name_snapshot || "",
      specification: line.specification_snapshot || "",
      quantity: Number(line.order_quantity || 1),
      unitCode: line.unit_code || "EA",
      requestedDueDate: line.requested_due_date || today(),
      standardLeadDays: Number(line.standard_lead_days || 0),
      planningBufferDays: Number(line.planning_buffer_days || 0),
      dayBasis: line.day_basis || "BUSINESS",
      productionPlanStatus: line.production_plan_status || "NOT_REVIEWED",
    })),
  };
};

export const OssOrderFormDialog = ({
  open,
  onClose,
  groupCode,
  projectCode,
  orderId,
  initialData,
  onSaved,
  onCreated,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);

  const isEdit = Boolean(orderId);
  const title = useMemo(() => (open ? (isEdit ? "주문 수정" : "주문 등록") : ""), [open, isEdit]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(isEdit ? normalizeDetailToForm(initialData) : emptyForm());
  }, [open, isEdit, initialData]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateLine = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line
      ),
    }));

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const removeLine = (index) =>
    setForm((prev) => ({
      ...prev,
      lines:
        prev.lines.length === 1 || prev.lines[index]?.orderLineId
          ? prev.lines
          : prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const saved = isEdit
        ? await updateOssOrder(groupCode, projectCode, orderId, form)
        : await createOssOrder(groupCode, projectCode, form);
      onCreated?.(saved);
      onSaved?.(saved);
      onClose?.();
      setForm(emptyForm());
    } catch (e) {
      setError(e.response?.data?.message || `주문 ${isEdit ? "수정" : "등록"} 중 오류가 발생했습니다.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          minHeight: 62,
          px: 2.5,
          py: 1.6,
          borderBottom: "1px solid #e5e7eb",
          fontSize: "20px !important",
          fontWeight: "800 !important",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              p: 2,
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              bgcolor: "#fff",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.4,
            }}
          >
            <TextField
              label="주문번호"
              value={form.orderNo}
              onChange={(e) => update("orderNo", e.target.value)}
              required
              disabled={isEdit}
              helperText={isEdit ? "주문번호는 등록 후 변경하지 않습니다." : ""}
            />
            <TextField label="거래처" value={form.customerName} onChange={(e) => update("customerName", e.target.value)} required />
            <TextField label="납품처/현장" value={form.siteName} onChange={(e) => update("siteName", e.target.value)} />
            <TextField label="고객 발주번호" value={form.customerPoNo} onChange={(e) => update("customerPoNo", e.target.value)} />
            <TextField type="date" label="주문일자" value={form.orderDate} onChange={(e) => update("orderDate", e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField select label="주문 상태" value={form.orderStatus} onChange={(e) => update("orderStatus", e.target.value)}>
              <MenuItem value="RECEIVED">주문 접수</MenuItem>
              <MenuItem value="CONFIRMED">주문 확정</MenuItem>
              <MenuItem value="HOLD">보류</MenuItem>
              <MenuItem value="DRAFT">임시 저장</MenuItem>
            </TextField>
            <TextField
              label="비고"
              value={form.note}
              onChange={(e) => update("note", e.target.value)}
              multiline
              minRows={2}
              sx={{ gridColumn: { xs: "auto", md: "1 / span 3" } }}
            />
          </Box>

          <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>주문 품목</Typography>
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addLine}>
                품목 추가
              </Button>
            </Stack>

            <Stack spacing={1.2}>
              {form.lines.map((line, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.4,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "120px minmax(180px, 1.2fr) minmax(160px, 1fr) 100px 80px 145px 130px 130px 44px",
                    },
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField label="품목코드" value={line.itemCode} onChange={(e) => updateLine(index, "itemCode", e.target.value)} size="small" />
                  <TextField label="품목명" value={line.itemName} onChange={(e) => updateLine(index, "itemName", e.target.value)} size="small" required />
                  <TextField label="규격" value={line.specification} onChange={(e) => updateLine(index, "specification", e.target.value)} size="small" />
                  <TextField type="number" label="수량" value={line.quantity} onChange={(e) => updateLine(index, "quantity", e.target.value)} size="small" />
                  <TextField label="단위" value={line.unitCode} onChange={(e) => updateLine(index, "unitCode", e.target.value)} size="small" />
                  <TextField type="date" label="요청 납기" value={line.requestedDueDate} onChange={(e) => updateLine(index, "requestedDueDate", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                  <TextField type="number" label="소요일" value={line.standardLeadDays} onChange={(e) => updateLine(index, "standardLeadDays", e.target.value)} size="small" />
                  <TextField type="number" label="여유일" value={line.planningBufferDays} onChange={(e) => updateLine(index, "planningBufferDays", e.target.value)} size="small" />
                  <IconButton
                    onClick={() => removeLine(index)}
                    disabled={form.lines.length === 1 || Boolean(line.orderLineId)}
                    title={line.orderLineId ? "등록된 품목 삭제는 주문 취소/삭제 흐름에서 처리합니다." : "품목 제거"}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.6, borderTop: "1px solid #e5e7eb" }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">취소</Button>
        <Button onClick={submit} disabled={submitting} variant="contained">
          {isEdit ? "수정 완료" : "등록 완료"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
